import {
  format,
  getDay,
  isBefore,
  startOfDay,
  addDays,
  isAfter,
  isSameDay,
} from "date-fns";
import { BookingAvailability } from "../../../api-contracts/sites/wbe/booking-availability";
import { LanguageCode } from "../api/contracts/language-settings";

/**
 * Check if a date is within one week from today
 */
export const isDateWithinOneWeek = (date: Date): boolean => {
  const today = startOfDay(new Date());
  // Today counts as day 1, so the 7-day window ends at today + 6 days.
  const oneWeekFromToday = addDays(today, 6);
  const dateOnly = startOfDay(date);
  return !isBefore(dateOnly, today) && !isBefore(oneWeekFromToday, dateOnly);
};

/**
 * Get the stop time for a given date (if it exists and is within one week)
 */
export const getStopTimeForDate = (
  date: Date,
  bookingAvailability: BookingAvailability | undefined,
) => {
  if (!bookingAvailability || !isDateWithinOneWeek(date)) return null;

  const dayOfWeek = getDay(date);
  const stopTime = bookingAvailability.stopTimes.find(
    (st) => st.arrivalDay === dayOfWeek,
  );

  return stopTime || null;
};

/**
 * Check if the stop time deadline has passed for a given date
 */
export const isStopTimeExpired = (
  date: Date,
  bookingAvailability: BookingAvailability | undefined,
): boolean => {
  const stopTime = getStopTimeForDate(date, bookingAvailability);
  if (!stopTime) return false;

  const stayDate = startOfDay(date);
  const stayDayOfWeek = getDay(stayDate);
  const daysBackToBookingDay = (stayDayOfWeek - stopTime.bookingDay + 7) % 7;
  const cutoffDate = addDays(stayDate, -daysBackToBookingDay);
  const [cutoffHour, cutoffMinute] = stopTime.bookingTime
    .split(":")
    .map(Number);
  cutoffDate.setHours(cutoffHour, cutoffMinute, 0, 0);

  return isAfter(new Date(), cutoffDate);
};

/**
 * The operator's stop-time notice for a date, in the language being browsed.
 *
 * Falls back along a named chain — the browsed language, then English, then
 * Swedish. The previous version sorted every language that wasn't Swedish into
 * an "en" bucket, so a German guest was shown the English notice even with a
 * German one stored; and its last resort was `entries[0]`, whichever language
 * happened to sit first in the stored array.
 *
 * No last-resort "any language that has text": the notice is only missing in
 * every language of the chain when the operator wrote none, and a date closed
 * without an explanation beats one explained in a language the guest can't
 * read.
 */
export const getStopTimeMessageForDate = (
  date: Date,
  bookingAvailability: BookingAvailability | undefined,
  languageCode: LanguageCode,
): string | null => {
  const stopTime = getStopTimeForDate(date, bookingAvailability);

  if (!stopTime) return null;

  const pick = (code: LanguageCode) =>
    stopTime.entries
      .find((e) => e.languageCode.toLowerCase() === code.toLowerCase())
      ?.message?.trim();

  return pick(languageCode) || pick("en-GB") || pick("sv-SE") || null;
};

/**
 * Get stop time message for the first blocked stay date in [arrival, departure).
 */
export const getStopTimeMessageForStayRange = (
  arrivalDate: Date,
  departureDate: Date,
  bookingAvailability: BookingAvailability | undefined,
  languageCode: LanguageCode,
): string | null => {
  const blockedDate = getFirstBlockedStayDateByStopTime(
    arrivalDate,
    departureDate,
    bookingAvailability,
  );

  if (!blockedDate) return null;

  return getStopTimeMessageForDate(blockedDate, bookingAvailability, languageCode);
};

/**
 * Get the first stay date blocked by an expired stop time in a date range.
 * The stay range includes arrival date and excludes departure date.
 */
export const getFirstBlockedStayDateByStopTime = (
  arrivalDate: Date,
  departureDate: Date,
  bookingAvailability: BookingAvailability | undefined,
): Date | null => {
  const arrival = startOfDay(arrivalDate);
  const departure = startOfDay(departureDate);

  if (!isAfter(departure, arrival)) return null;

  let currentDate = arrival;
  while (!isSameDay(currentDate, departure)) {
    if (isStopTimeExpired(currentDate, bookingAvailability)) {
      return currentDate;
    }
    currentDate = addDays(currentDate, 1);
  }

  return null;
};

/**
 * Check if any stay date in [arrival, departure) is blocked by expired stop time.
 */
export const isStayRangeBlockedByStopTime = (
  arrivalDate: Date,
  departureDate: Date,
  bookingAvailability: BookingAvailability | undefined,
): boolean =>
  getFirstBlockedStayDateByStopTime(
    arrivalDate,
    departureDate,
    bookingAvailability,
  ) !== null;

/**
 * Check if a date is unavailable for arrival purposes — either no vacancy
 * exists anywhere on that date (unavailableDates) or every category ad's
 * arrival window excludes it (arrivalOnlyRestrictedDates). The latter only
 * blocks ARRIVAL; the night itself is bookable as a middle-stay night, so
 * `getNextUnavailableDate` deliberately keeps reading `unavailableDates`
 * alone — see `isDepartureDateDisabled` below.
 */
export const isDateUnavailable = (
  date: Date,
  bookingAvailability: BookingAvailability | undefined,
): boolean => {
  if (!bookingAvailability) return false;
  const dateStr = format(date, "yyyy-MM-dd");
  return (
    bookingAvailability.unavailableDates.includes(dateStr) ||
    bookingAvailability.arrivalOnlyRestrictedDates?.includes(dateStr) === true
  );
};

/**
 * Categories that allow arrival on `arrivalDate` — both ad-arrival-window
 * permits and the category has vacancy + price on that night. Returns an
 * empty array when no category qualifies (arrival itself shouldn't have
 * been clickable in that case).
 */
const getArrivalCategoriesForDate = (
  arrivalDate: Date,
  bookingAvailability: BookingAvailability,
): { categoryId: string; bookableNights: Set<string> }[] => {
  const dateStr = format(arrivalDate, "yyyy-MM-dd");
  const result: { categoryId: string; bookableNights: Set<string> }[] = [];
  for (const cat of bookingAvailability.categories ?? []) {
    if (!cat.arrivalAllowedDates.includes(dateStr)) continue;
    const bookableNights = new Set(cat.bookableNightDates);
    if (!bookableNights.has(dateStr)) continue;
    result.push({ categoryId: cat.categoryId, bookableNights });
  }
  return result;
};

/**
 * Find the next unavailable date after a given date (for departure date selection).
 *
 * When `arrivalDate` is provided AND per-category data exists, the search is
 * restricted to the categories that permit arrival on `arrivalDate` — a
 * stay can only extend as long as ONE of those categories continuously
 * covers each stayed night. Without an arrival context (or per-category
 * data), falls back to site-wide `unavailableDates`.
 */
export const getNextUnavailableDate = (
  afterDate: Date,
  bookingAvailability: BookingAvailability | undefined,
  arrivalDate?: Date,
): Date | null => {
  if (!bookingAvailability) return null;

  const today = startOfDay(new Date());
  const maxCheckDate = addDays(today, 365);

  const arrivalCats = arrivalDate
    ? getArrivalCategoriesForDate(arrivalDate, bookingAvailability)
    : [];

  if (arrivalCats.length > 0) {
    // A real booking requires ONE single category to continuously cover
    // every stayed night. For each arrival-eligible category, walk forward
    // to find the first night it cannot book — that is its departure
    // boundary. A departure E is valid iff SOME category covers every
    // night in [arrival, E), so return the LATEST boundary across cats.
    // If any cat reaches `maxCheckDate` without a gap, departure is unbounded.
    let latestBoundary: Date | null = null;
    for (const cat of arrivalCats) {
      let firstGap: Date | null = null;
      let checkDate = addDays(afterDate, 1);
      while (checkDate <= maxCheckDate) {
        const dateStr = format(checkDate, "yyyy-MM-dd");
        if (!cat.bookableNights.has(dateStr)) {
          firstGap = checkDate;
          break;
        }
        checkDate = addDays(checkDate, 1);
      }
      if (!firstGap) return null;
      if (!latestBoundary || isAfter(firstGap, latestBoundary)) {
        latestBoundary = firstGap;
      }
    }
    return latestBoundary;
  }

  let checkDate = addDays(afterDate, 1);
  while (checkDate <= maxCheckDate) {
    const dateStr = format(checkDate, "yyyy-MM-dd");
    if (bookingAvailability.unavailableDates.includes(dateStr)) {
      return checkDate;
    }
    checkDate = addDays(checkDate, 1);
  }

  return null;
};

/**
 * A date is a valid departure when every stayed-on night between arrival and
 * that date is bookable. The departure date itself is the check-out day — guests
 * do not occupy a room that night — so its own availability does not matter.
 *
 * Concretely: with arrival = Mon and Tue marked unavailable, Tue is still a
 * valid departure (last night = Mon → Tue, Mon is bookable since Mon was a
 * valid arrival). Wed is not (last night = Tue → Wed requires Tue bookable).
 */
export const isDepartureDateDisabled = (
  date: Date,
  arrivalDate: Date,
  bookingAvailability: BookingAvailability | undefined,
): boolean => {
  if (isBefore(startOfDay(date), startOfDay(arrivalDate))) return true;
  if (isSameDay(date, arrivalDate)) return true;

  const nextUnavailable = getNextUnavailableDate(
    arrivalDate,
    bookingAvailability,
    arrivalDate,
  );
  if (!nextUnavailable) return false;
  return isAfter(startOfDay(date), startOfDay(nextUnavailable));
};
